import { FC, useCallback, useEffect, useRef, useState } from 'react';
import Page, { PageProps } from '../../components/page';
import { Box, Stack } from '@mui/material';
import DataTable, { DataTableInstance } from '../../components/data-table';
import PageHeader from '../../components/page/header';
import PageBlurb from '../../components/page/blurb';

import { DtColumn, DtFilter, DtSort } from '@/components/data-table/helpers';
import OpenDrawerButton from '@/components/drawers/open-drawer-button';
import { downloadOrgs, getOrgs } from '@/lib/services/org.service';
import { QueryKey } from '@/lib/query-client';
import Chip from '@/components/chip';
import { Actions } from '@/components/data-table/actions';
import { userCan } from '@/lib/services/auth.service';
import StatusChip, { ChipStatus } from '@/components/chip/status-chip';
import { uniqBy } from 'lodash';
import { getAllPartners, getPartnersByIds } from '@/lib/services/partner.service';
import Text from '@/components/text';
import useApp from '@/hooks/use-app.hook';
import { useQuery } from 'react-query';
import Button from '@/components/button';
import AssumeRoleMenu from '@/components/assume-role-menu';
import { OrgModeOptions } from '@/lib/models/org.model';
import NoneChip from '@/components/chip/none-chip';
import DomSize from '@/components/dom-size';
import useToast from '@/hooks/use-toast.hook';
import useDrawer from '@/hooks/use-drawer.hook';
import { PartnerModel } from '@/lib/models/partner.model';

const columns: DtColumn[] = [
  { name: 'name', label: 'Name', flex: 2, sortable: true },
  { name: 'domains', label: 'Domains', flex: 2, sortable: false },
  { name: 'status', label: 'Status', sortable: false },
  { name: 'mode', label: 'Mode', sortable: false },
  { name: 'actions', label: '', sortable: false },
];

const filter: DtFilter = {
  keyword: '',
  fields: [
    {
      columnName: 'status',
      label: 'Status',
      control: 'select-multi',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' },
      ],
    },
  ],
  values: [],
};

const POLL_MS = 10000;

const OrgsPage: FC<PageProps> = () => {
  const { openDrawer } = useDrawer();
  const [selectedRowId, setSelectedRowId] = useState('');
  const [activeFilter, setActiveFilter] = useState({ ...filter });
  const [poll, setPoll] = useState(POLL_MS);
  const { user } = useApp();
  const isSuper = userCan(user, 'super');
  const { errorToast } = useToast();

  const dtRef = useRef<DataTableInstance>(null);

  const handleRefresh = () => {
    dtRef.current?.refresh();
  };

  const handleOpenDrawer = useCallback(
    (id?: string) => {
      openDrawer('org', {
        id: id || null,
        onChange: handleRefresh,
        onClose: () => setSelectedRowId(''),
      });

      if (id) {
        setSelectedRowId(id);
      }
    },
    [openDrawer]
  );

  // we don't want polling to make any api calls while in the middle of assuming a role, so disable it while the assume menu is open
  const handleOpenAssumeMenu = () => {
    setPoll(0);
  };

  const handleCloseAssumeMenu = () => {
    setPoll(POLL_MS);
  };

  const handleExport = async (
    _page: number,
    _pageSize: number,
    sort?: DtSort,
    filter?: DtFilter
  ) => {
    return downloadOrgs(sort || { columnName: 'name', direction: 'asc' }, filter).then(
      (downloaded) => {
        if (!downloaded) {
          errorToast('Failed to generate download');
        }
      }
    );
  };

  const { data: partners } = useQuery([QueryKey.PartnerList], async () => getAllPartners(), {
    enabled: isSuper,
  });

  useEffect(() => {
    if (!partners) {
      return;
    }

    setActiveFilter((old) => {
      const updated = { ...old };

      const partnerFilterIndex = updated.fields.findIndex(
        ({ columnName }) => columnName === 'partnerId'
      );
      const partnerOpts = partners.map(({ id, name }) => ({ label: name, value: id }));

      if (partnerFilterIndex === -1) {
        updated.fields.push({
          columnName: 'partnerId',
          label: 'Partner',
          control: 'select-multi',
          options: partnerOpts,
        });

        return updated;
      }

      updated.fields[partnerFilterIndex].options = partnerOpts;

      return updated;
    });
  }, [partners]);

  const loadData = useCallback(
    async (page: number, pageSize: number, sort?: DtSort, filter?: DtFilter) => {
      const { rows, total } = await getOrgs(
        page,
        pageSize,
        sort || { columnName: 'name', direction: 'asc' },
        filter
      );

      let partners: PartnerModel[] = [];

      if (isSuper) {
        const partnerIds = uniqBy(rows, 'partnerId')
          .filter(({ partnerId }) => partnerId)
          .map(({ partnerId }) => partnerId);

        if (partnerIds.length) {
          partners = await getPartnersByIds(partnerIds);
        }
      }

      return {
        page,
        pageSize,
        sort,
        filter,
        rows: rows.map((row) => {
          const { id, name, domains, status, partnerId, mode } = row;
          const partner = partners.find(({ id }) => id === partnerId);
          const canAction = status !== 'deleting';

          return {
            id,
            name: (
              <Stack>
                <Box>{name}</Box>
                <Text size="small" color="grey">
                  {partner?.name}
                </Text>
              </Stack>
            ),
            domains: (
              <Stack gap={0.5} direction="row">
                {domains.map((domain) => (
                  <Chip key={domain} label={domain} size="small" />
                ))}
              </Stack>
            ),
            status: <StatusChip value={status as ChipStatus} size="small" />,
            mode: OrgModeOptions.find(({ value }) => value === mode)?.label || (
              <NoneChip notAvailable />
            ),
            actions: (
              <Actions>
                <AssumeRoleMenu
                  org={row}
                  onOpen={handleOpenAssumeMenu}
                  onClose={handleCloseAssumeMenu}
                />
                <OpenDrawerButton onClick={() => handleOpenDrawer(id)} disabled={!canAction} />
              </Actions>
            ),
          };
        }),
        total,
      };
    },
    [handleOpenDrawer, isSuper]
  );

  return (
    <Page title="Organizations">
      <PageHeader>
        <PageBlurb>
          Create new organizations, and view and change configurations for existing organizations.
        </PageBlurb>
        <Button
          label="Add Organization"
          icon="plus"
          size="small"
          onClick={() => handleOpenDrawer()}
        />
      </PageHeader>
      <DomSize>
        <DataTable
          queryKey={QueryKey.OrgsDataset}
          searchPlaceholder="Search by name or domain"
          columns={columns}
          onLoad={loadData}
          rowHeight={70}
          search
          filter={activeFilter}
          selectedRowId={selectedRowId}
          ref={dtRef}
          poll={poll}
          onExport={handleExport}
        />
      </DomSize>
    </Page>
  );
};

export default OrgsPage;
